import {
    ChangeEvent,
    FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormFeedback,
} from "reactstrap";
import Select from "react-select";

import { IRole, ActionType } from "Pages/Users/ManageUsers/ManageUsers";
import styles from "Pages/Admin/AdminConfiguration/AdminConfigModal/AdminConfigModal.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";

const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
    arr.reduce((groups, item) => {
        (groups[key(item)] ||= []).push(item);
        return groups;
    }, {} as Record<K, T[]>);

interface IProps {
    activeModal: ActionType;
    getPageData: () => Promise<void>;
    onSetActiveModal: (modal: ActionType) => void;
    onSetSelectedRow: (row: any) => void;
    selectedRow: any;
    roles: IRole[];
    companyUsersWithUsers: ICompanyUser[];
    integrations: IIntegration[];
}

export interface IUserPermissions {
    role?: string;
    process?: string;
    review?: string;
    abandon?: string;
    admin?: string;
    users?: string;
    config?: string;
    schedule?: string;
    connection?: string;
    matchTools?: string;
}

const defaultFormData = {
    user_id: "",
    name: "",
    email: "",
    password: "",
    enabled: true,
    activation_code: "",
    is_calance_admin: 0,
    permissions: "",
};

const defaultPermissions = {
    role: "",
    process: "",
    review: "",
    abandon: "",
    admin: "",
    users: "",
    config: "",
    schedule: "",
    connection: "",
    matchTools: "",
};

export default function ManageUsersModal({
    activeModal,
    getPageData,
    onSetActiveModal,
    onSetSelectedRow,
    selectedRow,
    roles,
    companyUsersWithUsers,
    integrations,
}: IProps) {
    const [formData, setFormData] = useState(defaultFormData);
    const [permissions, setPermissions] = useState<IUserPermissions>();

    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    const { handleFormChange, handlePermissions, isValidEmail } = useHelpers();
    const { post } = useFetch();

    // Set permissions for form data based on selected user. Permissions could be
    // part of formData state and probably should be.
    useEffect(() => {
        if (!companyUsersWithUsers.length || !formData.user_id) {
            return;
        }

        let company_user = companyUsersWithUsers.find(
            (v) => v.user_id === formData.user_id
        );
        let permissions = company_user?.permissions?.split(" | ");
        if (!permissions || !company_user) {
            return;
        }

        let role_index = permissions.indexOf(
            permissions.find((v) => v.includes("Role:"))!
        );
        let process_index = permissions.indexOf(
            permissions.find((v) => v.includes("Process:"))!
        );
        let review_index = permissions.indexOf(
            permissions.find((v) => v.includes("Review:"))!
        );
        let role =
            role_index !== -1
                ? permissions[role_index].split(":")[1]
                : undefined;
        let process =
            process_index !== -1
                ? permissions[process_index].split(":")[1]
                : undefined;
        let review =
            review_index !== -1
                ? permissions[review_index!].split(":")[1]
                : undefined;
        let abandon = company_user.permissions.includes("Abandon")
            ? "Abandon"
            : "";
        let admin = company_user.permissions.includes("Admin") ? "Admin" : "";
        let users = company_user.permissions.includes("Users") ? "Users" : "";
        let config = company_user.permissions.includes("Config")
            ? "Config"
            : "";
        let schedule = company_user.permissions.includes("Schedule")
            ? "Schedule"
            : "";
        let connection = company_user.permissions.includes("Connection")
            ? "Connection"
            : "";
        let matchTools = company_user.permissions.includes("MatchTools")
            ? "MatchTools"
            : "";

        setPermissions({
            role: role,
            process: process,
            review: review,
            abandon: abandon,
            admin: admin,
            users: users,
            config: config,
            schedule: schedule,
            connection: connection,
            matchTools: matchTools,
        });
        // formData.user_id changes in another useEffect when a row is selected
    }, [companyUsersWithUsers, formData.user_id]);

    // Set formData to default values when modal opens
    useEffect(() => {
        if (activeModal === "") return;

        if (activeModal.includes("new")) {
            setFormData(defaultFormData);
            setPermissions(defaultPermissions);
        }

        // Set formData to selected row values so values appear in inputs
        if (!activeModal.includes("new")) {
            setFormData({
                user_id: selectedRow.values.user_id
                    ? selectedRow.values.user_id
                    : defaultFormData.user_id,
                name: selectedRow.values.name
                    ? selectedRow.values.name
                    : defaultFormData.name,
                email: selectedRow.values.email
                    ? selectedRow.values.email
                    : defaultFormData.email,
                password: selectedRow.values.password
                    ? selectedRow.values.password
                    : defaultFormData.password,
                enabled: selectedRow.values.enabled
                    ? selectedRow.values.enabled
                    : defaultFormData.enabled,
                activation_code: selectedRow.values.activation_code
                    ? selectedRow.values.activation_code
                    : defaultFormData.activation_code,
                is_calance_admin: selectedRow.values.is_calance_admin
                    ? selectedRow.values.is_calance_admin
                    : defaultFormData.is_calance_admin,
                permissions: selectedRow.values.permissions
                    ? selectedRow.values.permissions
                    : defaultFormData.permissions,
            });
        }
    }, [activeModal, selectedRow]);

    const closeModal = useCallback(() => {
        onSetActiveModal("");
        setFormData(defaultFormData);
        setPermissions(defaultPermissions);
    }, [onSetActiveModal]);

    async function submitFormData(e: FormEvent) {
        e.preventDefault();

        const url = `/api/users/manage/${activeModal}`;

        const permissionsString = getPermissionsString();

        const body = {
            company_id: userContext.companyId,
            user_id: formData.user_id,
            name: formData.name,
            email: formData.email,
            enabled: formData.enabled,
            permissions: permissionsString,
        };
        const response = await post(url, body);
        let data;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(`Manage users modal request response: ${data}`);

        if (response.ok) {
            const message = activeModal.startsWith("new")
                ? "Request submitted. An activation email has been sent to the user"
                : "Request submitted successfully";
            newAlert({
                isOpen: true,
                message: message,
                color: "success",
            });
        } else {
            newAlert({
                isOpen: true,
                message: data.detail || "Failed to submit request",
                color: "danger",
            });
        }
        // Erase the selected row state
        onSetSelectedRow(undefined);
        // Fetch the company configurations again from the parent component to update the table
        getPageData();
        closeModal();
    }

    function getPermissionsString() {
        return (
            (permissions?.role
                ? "Role:" + permissions?.role + "[splitMarker]"
                : "") +
            (permissions?.process
                ? "Process:" + permissions?.process + "[splitMarker]"
                : "") +
            (permissions?.review
                ? "Review:" + permissions?.review + "[splitMarker]"
                : "") +
            (permissions?.abandon
                ? permissions?.abandon + "[splitMarker]"
                : "") +
            (permissions?.admin ? permissions?.admin + "[splitMarker]" : "") +
            (permissions?.users ? permissions?.users + "[splitMarker]" : "") +
            (permissions?.config ? permissions?.config + "[splitMarker]" : "") +
            (permissions?.schedule
                ? permissions?.schedule + "[splitMarker]"
                : "") +
            (permissions?.connection
                ? permissions?.connection + "[splitMarker]"
                : "") +
            (permissions?.matchTools ? permissions?.matchTools : "")
        )
            .split("[splitMarker]")
            .filter((x) => x !== "")
            .join(" | ");
    }

    let headerContent = "";
    switch (activeModal) {
        case "newProcore":
            headerContent = "Create New Procore User";
            break;
        case "newDimension":
            headerContent = "Create New Dimension User";
            break;
        case "edit":
            headerContent = `Change Permissions for ${formData.user_id}`;
            break;
        case "delete":
            headerContent = `Delete User ${formData.user_id}`;
            break;
    }

    const role_options = roles.map((role) => ({
        value: role.name,
        label: role.name,
    }));

    const process_grouped = groupBy(integrations, (i) => i.integration_group);
    const process_options = Object.entries(process_grouped)
        .map((integration) => ({
            label: integration[1][0]?.integration_group,
            options: integration[1].map((i) => ({
                value: i.process,
                label: i.name,
            })),
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));

    function togglePermission(e: ChangeEvent<HTMLInputElement>) {
        const key = e.target.id;
        const keyWithFirstLetterCapitalized =
            key.charAt(0).toUpperCase() + key.slice(1);

        let newPermissions = {};
        if (e.target.checked) {
            newPermissions = {
                ...permissions,
                [key]: keyWithFirstLetterCapitalized,
            };
        } else {
            newPermissions = { ...permissions, [key]: "" };
        }
        setPermissions(newPermissions);
    }

    // Next 3 getXYZPermissions functions are for setting the default values of the react-select inputs
    // that require an array of objects with value and label properties.
    function getRolePermissions() {
        if (permissions?.role && !activeModal.includes("new"))
            return permissions.role.split(",").map((r) => ({
                value: r,
                label: r,
            }));
    }

    // Process = integration
    function getProcessPermissions() {
        if (permissions?.process && !activeModal.includes("new"))
            return permissions.process.split(",").map((r) => ({
                value: r,
                label: r,
            }));
    }

    function getReviewPermissions() {
        if (permissions?.review && !activeModal.includes("new"))
            return permissions.review.split(",").map((r) => ({
                value: r,
                label: r,
            }));
    }

    const isNewDimension = activeModal === "newDimension";
    const isEditNonProcore =
        activeModal === "edit" &&
        (selectedRow?.values.email ||
            !isValidEmail(selectedRow?.values.user_id ?? ""));

    const showName = isNewDimension || isEditNonProcore;

    const bodyContent = (
        <Form id="form" onSubmit={submitFormData}>
            {/* User ID */}
            <FormGroup
                row
                className={styles.formGroup}
                hidden={activeModal === "edit"}
            >
                <Col sm={2}>
                    <Label for="user_id">Email:</Label>
                </Col>
                <Col sm={9}>
                    <Input
                        id="user_id"
                        name="user_id"
                        type="email"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.user_id}
                        disabled={activeModal === "edit" || activeModal === "delete"}
                        valid={
                            activeModal !== "delete" &&
                            formData.user_id.length > 3 &&
                            isValidEmail(formData.user_id)
                        }
                        invalid={
                            activeModal !== "delete" &&
                            formData.user_id.length > 3 &&
                            !isValidEmail(formData.user_id)
                        }
                    />
                    {activeModal !== "delete" && formData.user_id.length > 3 && (
                        <FormFeedback>
                            {activeModal.includes("new") ? "Must be a valid email." : null}
                        </FormFeedback>
                    )}
                </Col>
            </FormGroup>

            {/* Name */}
            {showName && (
                <FormGroup row className={styles.formGroup}>
                    <Col sm={2}>
                        <Label for="name">Name:</Label>
                    </Col>
                    <Col sm={9}>
                        <Input
                            id="name"
                            name="name"
                            className={styles.input}
                            minLength={3}
                            maxLength={100}
                            required
                            onChange={(e) =>
                                handleFormChange(e, setFormData)
                            }
                            value={formData.name}
                        />
                    </Col>
                </FormGroup>
            )}
                        
            {/* Process */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="process">Process:</Label>
                </Col>
                <Col sm={9}>
                    <Select
                        classNamePrefix="rs"
                        options={process_options}
                        value={getProcessPermissions()}
                        isMulti
                        onChange={(e) =>
                            handlePermissions(e, setPermissions, "Process:")
                        }
                        isDisabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Role */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="role">Role:</Label>
                </Col>
                <Col sm={9}>
                    <Select
                        classNamePrefix="rs"
                        options={role_options}
                        value={getRolePermissions()}
                        isMulti
                        onChange={(e) =>
                            handlePermissions(e, setPermissions, "Role:")
                        }
                        isDisabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Review */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="Review">Review:</Label>
                </Col>
                <Col sm={9}>
                    <Select
                        classNamePrefix="rs"
                        options={[
                            { value: "ViewAll", label: "View All" },
                            { value: "RerunAll", label: "Rerun All" },
                        ]}
                        value={getReviewPermissions()}
                        isMulti
                        onChange={(e) =>
                            handlePermissions(e, setPermissions, "Review:")
                        }
                        isDisabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Checkbox inputs */}
            {/* Abandon */}
            <FormGroup
                row
                className={styles.formGroup}
                // Abandon is no longer used, so it is hidden. Only calance admins can abandon. All references to abandon should be removed
                // from this file.
                style={{ display: "none" }}
            >
                <Col sm={2}>
                    <Label for="abandon">Abandon:</Label>
                </Col>
                <Col sm={2}>
                    <Input
                        sm={1}
                        id="abandon"
                        name="abandon"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.abandon !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Admin */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="admin">Admin:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="admin"
                        name="admin"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.admin !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Config */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="config">Config:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="config"
                        name="config"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.config !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Connection */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="connection">Connection:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="connection"
                        name="connection"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.connection !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Match Tools */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="matchTools">Match Tools:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="matchTools"
                        name="matchTools"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.matchTools !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Schedule */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="schedule">Schedule:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="schedule"
                        name="schedule"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.schedule !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>

            {/* Users */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="Users">Users:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="users"
                        name="users"
                        type="checkbox"
                        className={styles.input}
                        onChange={(e) => togglePermission(e)}
                        checked={permissions?.users !== ""}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>
        </Form>
    );

    return (
        <Modal
            isOpen={activeModal !== "" && activeModal !== "password"}
            toggle={closeModal}
        >
            <ModalHeader toggle={closeModal}>{headerContent}</ModalHeader>
            <ModalBody>{bodyContent}</ModalBody>
            <ModalFooter id="modal_footer">
                <Button
                    color="primary"
                    type="submit"
                    form="form"
                    disabled={
                        (activeModal === "edit" || activeModal === "delete") &&
                        !selectedRow
                    }
                >
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
